import {
    DebiasTextResponse,
    DebiasPdfResponse,
    QuotaSnapshot,
} from "@/types/chat";
import { logSequenceMatcherStyleDiff } from "@/utils/logSequenceMatcherDiff";
import { getClientId } from "@/utils/clientId";
import {
    bumpLocalQuota,
    getLocalQuotaSnapshot,
    isQuotaSnapshot,
    parseQuotaFromResponseHeaders,
} from "@/utils/clientQuota";

/** Ίδιο παράθυρο με το proxy (`server/index.ts`) ώστε το κείμενο να μην κόβεται στα 60s. */
const LLM_REQUEST_TIMEOUT_MS = 300_000;

export class ChatService {
    private static instance: ChatService;
    private baseUrl: string;
    /** Έχουμε λάβει quota από GET /api/quota ή από headers απάντησης (όχι μόνο τοπικό fallback). */
    private remoteQuotaObserved = false;
    private lastRemoteQuota: QuotaSnapshot | null = null;

    private constructor() {
        const raw = import.meta.env.VITE_API_BASE_URL ?? "";
        this.baseUrl = raw.replace(/\/$/, "");
        if (!this.baseUrl) {
            // Κενό = ίδιο origin (Vite dev) → `/api/*` πάει στο proxy του vite.config.js
            this.baseUrl = "";
        }
    }

    public static getInstance(): ChatService {
        if (!ChatService.instance) {
            ChatService.instance = new ChatService();
        }
        return ChatService.instance;
    }

    private async fetchWithTimeout(
        input: RequestInfo | URL,
        init: RequestInit = {},
        timeoutMs = 300_000,
    ): Promise<Response> {
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

        try {
            return await fetch(input, {
                ...init,
                signal: controller.signal,
            });
        } catch (error) {
            if (error instanceof DOMException && error.name === "AbortError") {
                throw new Error(
                    "Το αίτημα καθυστέρησε υπερβολικά. Δοκιμάστε μικρότερο κείμενο ή ξανά σε λίγο.",
                );
            }
            throw error;
        } finally {
            clearTimeout(timeoutId);
        }
    }

    private commonHeaders(requestType: "message" | "file" | "quota"): HeadersInit {
        return {
            "X-Client-Id": getClientId(),
            "X-Request-Type": requestType,
        };
    }

    private rememberRemoteQuota(q: QuotaSnapshot): void {
        this.lastRemoteQuota = q;
        this.remoteQuotaObserved = true;
    }

    /**
     * GET /api/quota υπάρχει στο Node proxy. Αν το `VITE_API_BASE_URL` δείχνει απευθείας στο LLM
     * (π.χ. :8000) χωρίς αυτό το endpoint, επιστρέφουμε τοπικό μέτρημα ή cache.
     */
    async getQuota(): Promise<QuotaSnapshot> {
        try {
            const response = await this.fetchWithTimeout(
                `${this.baseUrl}/api/quota`,
                {
                    method: "GET",
                    headers: this.commonHeaders("quota"),
                },
                15_000,
            );

            if (response.ok) {
                const data: unknown = await response.json();
                if (isQuotaSnapshot(data)) {
                    this.rememberRemoteQuota(data);
                    return data;
                }
            }
        } catch {
            // δίκτυο / CORS / χωρίς route
        }

        if (this.lastRemoteQuota) {
            return this.lastRemoteQuota;
        }

        return getLocalQuotaSnapshot();
    }

    /** Μετά από επιτυχημένο POST: headers (proxy) ή τοπικό +1 αν δεν υπάρχει remote quota. */
    private syncQuotaAfterSuccessfulRequest(
        response: Response,
        kind: "message" | "file",
    ): void {
        const fromHeaders = parseQuotaFromResponseHeaders(response);
        if (fromHeaders) {
            this.rememberRemoteQuota(fromHeaders);
            return;
        }
        if (!this.remoteQuotaObserved) {
            const next = bumpLocalQuota(kind);
            this.lastRemoteQuota = next;
        }
    }

    async sendMessage(
        message: string,
        scenario: "A" | "B",
    ): Promise<{ debiased: string; originalForDiff: string }> {
        const response = await this.fetchWithTimeout(
            `${this.baseUrl}/api/text`,
            {
                method: "POST",
                headers: {
                    ...this.commonHeaders("message"),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ text: message, scenario }),
            },
            LLM_REQUEST_TIMEOUT_MS,
        );

        if (!response.ok) {
            const fromHeaders = parseQuotaFromResponseHeaders(response);
            if (fromHeaders) {
                this.rememberRemoteQuota(fromHeaders);
            }
            let errorMessage = `Request failed with status ${response.status}`;
            try {
                const errorData = (await response.json()) as {
                    detail?: string;
                    message?: string;
                    error?: string;
                };
                if (isQuotaSnapshot(errorData)) {
                    this.rememberRemoteQuota(errorData);
                }
                errorMessage =
                    errorData.detail || errorData.message || errorData.error || errorMessage;
            } catch {
                // keep fallback message
            }
            throw new Error(errorMessage);
        }

        this.syncQuotaAfterSuccessfulRequest(response, "message");

        const data = (await response.json()) as DebiasTextResponse;

        const originalText = Array.isArray(data.original)
            ? data.original.join(" ")
            : data.original;

        const debiasedText = Array.isArray(data.debiased)
            ? data.debiased.join(" ")
            : data.debiased;

        logSequenceMatcherStyleDiff(originalText, debiasedText);

        return {
            debiased: debiasedText,
            originalForDiff: originalText,
        };
    }

    async sendPdf(
        file: File,
    ): Promise<{ debiased: string; originalForDiff: string }> {
        const formData = new FormData();
        formData.append("file", file);

        const response = await this.fetchWithTimeout(
            `${this.baseUrl}/api/pdf`,
            {
                method: "POST",
                headers: this.commonHeaders("file"),
                body: formData,
            },
            LLM_REQUEST_TIMEOUT_MS,
        );

        if (!response.ok) {
            const fromHeaders = parseQuotaFromResponseHeaders(response);
            if (fromHeaders) {
                this.rememberRemoteQuota(fromHeaders);
            }
            let errorMessage = `Request failed with status ${response.status}`;
            try {
                const errorData = (await response.json()) as {
                    detail?: string;
                    message?: string;
                    error?: string;
                };
                if (isQuotaSnapshot(errorData)) {
                    this.rememberRemoteQuota(errorData);
                }
                errorMessage =
                    errorData.detail || errorData.message || errorData.error || errorMessage;
            } catch {
                // keep fallback message
            }
            throw new Error(errorMessage);
        }

        this.syncQuotaAfterSuccessfulRequest(response, "file");

        const data = (await response.json()) as DebiasPdfResponse;
        const originalText = data.original.join(" ");
        const debiasedStr = data.debiased.join(" ");

        logSequenceMatcherStyleDiff(originalText, debiasedStr);

        return { debiased: debiasedStr, originalForDiff: originalText };
    }
}